import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Alert, Button, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { combineComparers, stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../api/adminApi";
import type { Category } from "../../../../../api/types/accessTypes";
import type {
  Contact,
  ContactWithFieldValues,
  Investor,
  InvestorContact,
  InvestorContactPermissions,
} from "../../../../../api/types/fundStructureTypes";
import AddContactDialog from "../../common/AddContactDialog";
import CommunicationMatrix from "../../common/CommunicationMatrix";
import { InvestorMatrixContactGroupCell, MatrixFundRowCell } from "../../common/matrixGridComponents";
import type { CommunicationMatrixModel } from "../../common/matrixHelper";
import {
  cloneInvestorModelWithoutChildren,
  createPermissionModelsForContacts,
  getContactChangesWhereInvitationWouldBeSent,
  getGroupNodeModel,
  getId,
  getInvestorMatrixTreeDataPath,
  getModelId,
} from "../../common/matrixHelper";
import AssignInvestorContactDialog from "./AssignInvestorContactDialog";

interface Props {
  investor: Investor;
  permissions: InvestorContactPermissions[];
  categories: Category[];
  contacts: ContactWithFieldValues[];
}

const sortModelFunc = combineComparers<CommunicationMatrixModel>(
  stringComparerBy((m) => m.contactName),
  stringComparerBy((m) => m.fundName)
);

const InvestorCommunicationMatrix = ({ permissions, categories, investor, contacts }: Props) => {
  const originalModels: CommunicationMatrixModel[] = useMemo(() => {
    const available: CommunicationMatrixModel[] = createPermissionModelsForContacts(investor, investor.contacts);
    return available.map((model) => {
      const permissionsForModel =
        permissions.find((p) => getId(investor.title, p.fundInvestorId, p.contactId) === getModelId(model)) ?? [];
      return { ...model, ...permissionsForModel };
    });
  }, [investor, permissions]);

  const [contactsList, setContactsList] = useState<Contact[]>([...contacts]);
  const [initialModels, setInitialModels] = useState<CommunicationMatrixModel[]>(() => structuredClone(originalModels));
  const [models, setModels] = useState<CommunicationMatrixModel[]>(() => structuredClone(originalModels));
  const [showAssignContactDialog, setShowAssignContactDialog] = useState(false);
  const [showContactCreateDialog, setShowContactCreateDialog] = useState(false);

  const contactsForOptions = useMemo(() => {
    return contacts
      .filter((c) => !models.some((model) => model.contactId === c.id)) //exclude already added
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [contacts, models]);

  const { existingContactNames, existingContactEmails } = useMemo(() => {
    const existingContactNames = new Set<string>(contacts.map((c) => c.name));
    const existingContactEmails = new Set<string>(contacts.map((c) => c.email.toLowerCase()));
    return { existingContactNames, existingContactEmails };
  }, [contacts]);

  const createModelsForAddedContacts = (addedContacts: InvestorContact[]) => {
    setModels((currentModels) => [...currentModels, ...createPermissionModelsForContacts(investor, addedContacts)]);
  };

  const handleContactCreated = (newContact: Contact) => {
    createModelsForAddedContacts([{ contactId: newContact.id, name: newContact.name, email: newContact.email }]);
    setContactsList((prev) => [...prev, newContact]);
  };

  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      {investor.funds.length === 0 && (
        <Alert severity="warning">
          Investor does not have any fund. Please add a commitment to this investor in Fund Ops
        </Alert>
      )}
      <CommunicationMatrix
        entityType="Investor"
        noItemsMessage={"This investor doesn't have any contacts yet."}
        renderGroupCellElement={(value, node) => (
          <InvestorMatrixContactGroupCell value={value as string} contact={getGroupNodeModel(models, node)} />
        )}
        renderGridToolbarComponent={(isLoading: boolean) => (
          <Button
            startIcon={<AddRoundedIcon />}
            disabled={isLoading || investor.funds.length === 0}
            onClick={() => setShowAssignContactDialog(true)}
            variant="contained"
            color="primary"
          >
            Add Contact
          </Button>
        )}
        categories={categories}
        initialModels={initialModels}
        setInitialModels={setInitialModels}
        models={models}
        setModels={setModels}
        getChangesWhereInvitationWouldBeSent={(
          initialModels: CommunicationMatrixModel[],
          modifiedModels: CommunicationMatrixModel[]
        ) => getContactChangesWhereInvitationWouldBeSent(contactsList, initialModels, modifiedModels)}
        sortModelFunc={sortModelFunc}
        getTreeDataPath={getInvestorMatrixTreeDataPath}
        cloneModelWithoutChildren={cloneInvestorModelWithoutChildren}
        renderGroupedRowCell={(model: CommunicationMatrixModel) => <MatrixFundRowCell model={model} />}
        childRowActionsDisabled
        updateInvestorCommunicationMatrix={adminApi.updateInvestorCommunicationMatrix}
        excludedColumns={["isPrimary"]}
      />
      <AssignInvestorContactDialog
        open={showAssignContactDialog}
        onClose={() => setShowAssignContactDialog(false)}
        title={`Add contact(s) to ${investor.title}`}
        description={"Select the contacts you wish to associate with this investor."}
        onCreateNew={() => {
          setShowAssignContactDialog(false);
          setShowContactCreateDialog(true);
        }}
        options={contactsForOptions}
        onContactsSelected={(selectedContacts: Contact[]) => {
          const investorContacts: InvestorContact[] = selectedContacts.map((contact) => ({
            contactId: contact.id,
            name: contact.name,
            email: contact.email,
          }));
          createModelsForAddedContacts(investorContacts);
          setShowAssignContactDialog(false);
        }}
      />
      {showContactCreateDialog && (
        <AddContactDialog
          mode="create"
          onClose={() => setShowContactCreateDialog(false)}
          onCreate={handleContactCreated}
          existingContactNames={existingContactNames}
          existingContactEmails={existingContactEmails}
        />
      )}
    </Stack>
  );
};

export default InvestorCommunicationMatrix;
